export type QuestionLogStatusSyncPatch = {
  questionId: number;
  starred?: boolean;
  starredAt?: string | Date | null;
  markedForReview?: boolean;
  markedForReviewAt?: string | Date | null;
};

type QuestionLogStatusSyncField = Exclude<
  keyof QuestionLogStatusSyncPatch,
  'questionId'
>;

const QUESTION_LOG_STATUS_SYNC_EVENT = 'question-log-status-sync';
const latestQuestionLogStatusPatches = new Map<number, QuestionLogStatusSyncPatch>();

export const getQuestionLogStatusPatches = () =>
  Array.from(latestQuestionLogStatusPatches.values());

export const clearQuestionLogStatusPatch = (questionId: number) => {
  latestQuestionLogStatusPatches.delete(questionId);
};

export const clearQuestionLogStatusPatchFields = (
  questionId: number,
  fields: QuestionLogStatusSyncField[],
) => {
  const currentPatch = latestQuestionLogStatusPatches.get(questionId);

  if (!currentPatch) {
    return;
  }

  const nextPatch = { ...currentPatch };

  fields.forEach((field) => {
    delete nextPatch[field];
  });

  if (Object.keys(nextPatch).length <= 1) {
    latestQuestionLogStatusPatches.delete(questionId);
    return;
  }

  latestQuestionLogStatusPatches.set(questionId, nextPatch);
};

export const dispatchQuestionLogStatusPatched = (
  patch: QuestionLogStatusSyncPatch,
) => {
  if (!Number.isInteger(patch.questionId) || patch.questionId <= 0) {
    return;
  }

  const previousPatch = latestQuestionLogStatusPatches.get(patch.questionId);
  const nextPatch = { ...previousPatch, ...patch };
  latestQuestionLogStatusPatches.set(patch.questionId, nextPatch);

  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<QuestionLogStatusSyncPatch>(
      QUESTION_LOG_STATUS_SYNC_EVENT,
      {
        detail: nextPatch,
      },
    ),
  );
};

export const subscribeQuestionLogStatusPatched = (
  handler: (patch: QuestionLogStatusSyncPatch) => void,
) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const listener: EventListener = (event) => {
    const detail = (event as CustomEvent<QuestionLogStatusSyncPatch>).detail;

    if (!detail) {
      return;
    }

    handler(detail);
  };

  window.addEventListener(QUESTION_LOG_STATUS_SYNC_EVENT, listener);

  return () => {
    window.removeEventListener(QUESTION_LOG_STATUS_SYNC_EVENT, listener);
  };
};
